import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { createSpace } from '../../services/spaceService';
import { loadOrgSpaces, upsertSpace } from '../../store/slices/spaceSlice';
import { spaceTone, spaceInitials, SPACE_ICON_PRESETS } from '../../utils/spaceUtils';

const SpacePicker = ({
  valueId = '',
  valueName = '',
  onChange,
  disabled = false,
  allowCreate = true,
  locked = false,
  lockedHint = '',
  label = 'Space',
  required = false,
  variant = 'form',
}) => {
  const dispatch = useDispatch();
  const spaces = useSelector((state) => state.spaces.list || []);
  const spacesFetchedOnce = useSelector((state) => Boolean(state.spaces.fetchedOnce));
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [draftIcon, setDraftIcon] = useState('');
  const [error, setError] = useState('');
  const [menuPos, setMenuPos] = useState(null);
  const rootRef = useRef(null);
  const dropdownRef = useRef(null);
  const addInputRef = useRef(null);

  useEffect(() => {
    if (spacesFetchedOnce) return undefined;
    let cancelled = false;
    setLoading(true);
    dispatch(loadOrgSpaces())
      .unwrap()
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, spacesFetchedOnce]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (rootRef.current?.contains(event.target) || dropdownRef.current?.contains(event.target)) {
        return;
      }
      setOpen(false);
      setAdding(false);
      setDraft('');
      setDraftIcon('');
      setError('');
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  useEffect(() => {
    if (!open) {
      setMenuPos(null);
      return undefined;
    }
    const update = () => {
      const el = rootRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: Math.max(rect.width, 224),
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open]);

  const options = useMemo(() => {
    const list = [...spaces];
    if (valueId && !list.some((space) => space.id === valueId)) {
      list.unshift({ id: valueId, name: valueName || 'Space' });
    }
    return list.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  }, [spaces, valueId, valueName]);

  const selectSpace = (space) => {
    onChange?.({ spaceId: space?.id || '', spaceName: space?.name || '' });
    setOpen(false);
    setAdding(false);
    setDraft('');
        setDraftIcon('');
    setError('');
  };

  const startAdd = () => {
    setAdding(true);
    setError('');
    window.setTimeout(() => addInputRef.current?.focus(), 0);
  };

  const handleCreate = async () => {
    const name = draft.trim();
    if (!name || creating) return;
    setCreating(true);
    setError('');
    try {
      const created = await createSpace(name, draftIcon || undefined);
      dispatch(upsertSpace(created));
      selectSpace(created);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Could not create space');
    } finally {
      setCreating(false);
    }
  };

  const selectedTone = spaceTone(valueId || valueName || 'space');
  const compact = variant === 'combo';

  if (locked) {
    return (
      <div>
        {compact ? null : (
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{label}</label>
        )}
        <div className={`inline-flex items-center h-8 px-2.5 rounded-lg border text-xs font-semibold ${selectedTone.chip}`}>
          {valueName || 'Inherited from workflow'}
        </div>
        {lockedHint ? (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{lockedHint}</p>
        ) : null}
      </div>
    );
  }

  const dropdown = open && menuPos ? (
    <div
      ref={dropdownRef}
      className="fixed z-[1200] max-h-64 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg"
      style={{ top: menuPos.top, left: menuPos.left, width: menuPos.width }}
    >
      {loading ? (
        <p className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">Loading spaces…</p>
      ) : null}
      {!loading && options.length === 0 && !adding ? (
        <p className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">No spaces yet</p>
      ) : null}
      {options.map((space) => {
        const tone = spaceTone(space.id || space.name);
        const selected = space.id === valueId;
        return (
          <button
            key={space.id}
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => selectSpace(space)}
            className={`w-full flex items-center gap-2 text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 ${
              selected ? 'font-semibold' : 'font-medium'
            }`}
          >
            <span className="w-6 h-6 flex-shrink-0 inline-flex items-center justify-center text-sm">
              {space.icon || spaceInitials(space.name)}
            </span>
            <span className={`min-w-0 truncate px-1.5 py-0.5 rounded ${tone.chip}`}>{space.name}</span>
          </button>
        );
      })}
      {allowCreate ? (
        <div className="border-t border-gray-100 dark:border-gray-700">
          {adding ? (
            <div className="p-2 space-y-1.5">
              <input
                ref={addInputRef}
                value={draft}
                disabled={creating}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleCreate();
                  }
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    setAdding(false);
                    setDraft('');
        setDraftIcon('');
                  }
                }}
                placeholder="New space name"
                className="w-full h-8 px-2.5 rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-xs text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/40"
              />
              <div className="flex flex-wrap gap-1">
                {SPACE_ICON_PRESETS.map((icon) => (
                  <button
                    key={icon}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setDraftIcon((current) => (current === icon ? '' : icon))}
                    className={`h-7 w-7 text-sm ${
                      draftIcon === icon ? 'bg-primary-100 dark:bg-primary-900/40 ring-1 ring-primary-400' : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                    title="Space icon"
                  >
                    {icon}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleCreate}
                  disabled={creating || !draft.trim()}
                  className="h-7 px-2.5 rounded-md text-[11px] font-semibold text-white bg-primary-600 hover:bg-primary-500 disabled:opacity-40"
                >
                  {creating ? 'Adding…' : 'Add'}
                </button>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setAdding(false);
                    setDraft('');
        setDraftIcon('');
                    setError('');
                  }}
                  className="h-7 px-2 text-[11px] font-semibold text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={startAdd}
              className="w-full text-left px-3 py-2 text-sm font-semibold text-primary-700 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/30"
            >
              Add new space
            </button>
          )}
        </div>
      ) : null}
    </div>
  ) : null;

  const trigger = (
    <button
      type="button"
      disabled={disabled}
      onClick={() => {
        setOpen((prev) => !prev);
        setAdding(false);
        setDraft('');
        setDraftIcon('');
        setError('');
      }}
      className={`w-full inline-flex items-center gap-1.5 ${
        compact ? 'h-8 px-2.5 text-xs' : 'h-10 px-3 text-sm'
      } rounded-lg border text-left font-semibold hover:opacity-90 disabled:opacity-50 ${
        valueId || valueName
          ? selectedTone.chip
          : 'border-dashed border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400'
      }`}
      title={valueName || label}
      aria-label={label}
      aria-expanded={open}
    >
      {valueId || valueName ? (
        <span className={`h-2 w-2 flex-shrink-0 rounded-full ${selectedTone.dot}`} />
      ) : null}
      <span className="truncate flex-1 min-w-0">{valueName || (loading ? 'Loading…' : 'Space')}</span>
      <svg className="w-3.5 h-3.5 flex-shrink-0 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </button>
  );

  return (
    <div ref={rootRef} className={compact ? 'relative flex-shrink-0 w-[11.5rem] sm:w-[13.5rem]' : 'relative'}>
      {compact ? null : (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {label}{required ? <span className="text-rose-500"> *</span> : null}
        </label>
      )}
      {trigger}
      {dropdown && typeof document !== 'undefined'
        ? ReactDOM.createPortal(dropdown, document.body)
        : null}
      {error ? (
        <p className={compact
          ? 'absolute left-0 top-full mt-1 text-[10px] text-rose-500 whitespace-nowrap'
          : 'mt-1 text-xs text-rose-500'}
        >
          {error}
        </p>
      ) : null}
    </div>
  );
};

export default SpacePicker;
